import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import { useAddLinkedMemberMutation, useSearchHubspotContactsMutation } from 'store/api/version-api.slice';
import { debounce } from 'utils';
import { LoaderButton } from 'components/ProgressButton';

import { prepareContact } from '../utils';

import SearchLinkedMemberInput from './SearchLinkedMemberInput';

const AddLinkedMemberDialog = ({ isOpen, onClose, isAllowedToAdd }) => {
	const { primary_member } = useParams();

	const [search, { data = {}, isLoading }] = useSearchHubspotContactsMutation();
	const [addMember, { isLoading: isAddLoading }] = useAddLinkedMemberMutation();

	const { control, handleSubmit, setValue, reset } = useForm({ defaultValues: { contacts_search: null } });

	const handleSearch = debounce((event, newInputValue) => {
		search({ searchQuery: newInputValue });
	});

	const contacts = useMemo(() => {
		return data.contacts?.map(contact => prepareContact(contact)) ?? [];
	}, [data]);

	const handleApply = async formData => {
		if (isAllowedToAdd) {
			const response = await addMember({
				primary_member,
				new_secondary_member: formData.contacts_search?.id
			});

			if (response.error) {
				toast(response.error.data, { type: 'error' });
			} else {
				toast(response.data.msg, { type: 'info' });
				reset();
				onClose();
			}
		}
	};

	return (
		<Dialog open={isOpen} onClose={onClose} maxWidth={'sm'} fullWidth={true}>
			<DialogTitle>
				<Typography sx={{ fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
					<PersonAddIcon sx={{ mr: 1 }} /> Add new linked member
				</Typography>
			</DialogTitle>
			<DialogContent>
				<IconButton sx={{ position: 'absolute', top: 5, right: 5 }} onClick={onClose}>
					<CloseIcon />
				</IconButton>
				<form onSubmit={handleSubmit(handleApply)}>
					<Stack spacing={1} sx={{ pt: 1 }}>
						<SearchLinkedMemberInput
							options={contacts}
							textFieldProps={{ label: 'Search for contact' }}
							onInputChange={handleSearch}
							loading={isLoading}
							name="contacts_search"
							control={control}
							setValue={setValue}
						/>
						<Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography sx={{ fontSize: '13px', color: '#999', display: 'flex', alignItems: 'center' }}>
								<InfoIcon sx={{ mr: 1 }} />
								You can search for contacts using its name or email
							</Typography>
							<LoaderButton
								loading={isAddLoading}
								type="submit"
								variant="contained"
								circleSize={16}
								sx={{ maxWidth: 100, width: '100%', alignSelf: 'flex-end' }}
							>
								Apply
							</LoaderButton>
						</Stack>
					</Stack>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default AddLinkedMemberDialog;
